import React, { useState, useRef, useEffect } from "react";
import useStyles from "./style";
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { Close, LocationOn, Search, CheckCircle, AccessTime, PinDrop, EventSeat, DirectionsCar } from "@mui/icons-material";
import { CUSTOM_CONSTANTS } from "Constants";

// Components
import DistanceAndRate from "./DistanceDuration";
import Estimatedcarousel from "./Estimatedcarousel";
import CreateTrip from "./Createtripform";

// Services
import DispatcherApi from "Services/Dispatcher";

const PAYMENT_MODES = ["CASH", "CARD", "WALLET"];

// "5.2 km" → 5.2
function parseDistanceKm(distStr) {
  return parseFloat(String(distStr || "").replace(/km|mi/gi, "").trim()) || 0;
}

// "15 mins" / "1 hour 5 mins" → total minutes
function parseDurationMinutes(durStr) {
  const parts = String(durStr || "").match(/(\d+)\s*hours?|(\d+)\s*mins?/g) || [];
  return parts.reduce((total, part) => {
    if (part.includes("hour")) return total + parseInt(part, 10) * 60;
    if (part.includes("min")) return total + parseInt(part, 10);
    return total;
  }, 0);
}

const BookingForm = ({
  setOpen, setOpenbooking, openbooking,
  setOrigin, setDestination, origin, destination, setMapCenter,
  services, timezone, userType, subcmpnyName, userId,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  // Locations
  const [pickupLatLng, setPickupLatLng] = useState(null);
  const [dropLatLng, setDropLatLng] = useState(null);
  const [pickupInput, setPickupInput] = useState("");
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropInput, setDropInput] = useState("");
  const [dropSuggestions, setDropSuggestions] = useState([]);

  // Distance / duration (Google Maps)
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [travelDuration, setTravelduration] = useState("");
  const activecarouselRef = useRef(null);

  // Fare estimate
  const [fareEstimate, setFareEstimate] = useState(null);
  const [fareLoading, setFareLoading] = useState(false);
  const [fareError, setFareError] = useState("");
  const [fareSearched, setFareSearched] = useState(false);

  // Service selection + payment
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMode, setPaymentMode] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);

  const autocompleteService = useRef(null);
  const placesServiceRef = useRef(null);

  const bothLocationsSet = Boolean(pickupLatLng && dropLatLng);

  // Reset everything when locations change
  useEffect(() => {
    setFareSearched(false);
    setFareEstimate(null);
    setFareError("");
    setSelectedService(null);
    setPaymentMode("");
    setSendError("");
    setSendSuccess(false);
  }, [pickupLatLng, dropLatLng]);

  // Reset payment/send state when service changes
  useEffect(() => {
    setPaymentMode("");
    setSendError("");
    setSendSuccess(false);
  }, [selectedService]);

  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  useEffect(() => {
    if (!placesServiceRef.current && window.google && document.getElementById("map-temp")) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.getElementById("map-temp")
      );
    }
  }, []);

  const handlePickupChange = (e) => {
    const value = e.target.value;
    setPickupInput(value);
    if (value.length >= 6 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions({ input: value }, (predictions, status) => {
        setPickupSuggestions(
          status === window.google.maps.places.PlacesServiceStatus.OK ? predictions : []
        );
      });
    } else {
      setPickupSuggestions([]);
    }
  };

  const handleDropChange = (e) => {
    const value = e.target.value;
    setDropInput(value);
    if (value.length >= 6 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions({ input: value }, (predictions, status) => {
        setDropSuggestions(
          status === window.google.maps.places.PlacesServiceStatus.OK ? predictions : []
        );
      });
    } else {
      setDropSuggestions([]);
    }
  };

  const handlePickupSelect = (placeId, description) => {
    placesServiceRef.current.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setPickupLatLng({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
        setOrigin(description);
        setPickupInput(description);
        setPickupSuggestions([]);
        setDistance(null);
        setDuration(null);
      }
    });
  };

  const handleDropSelect = (placeId, description) => {
    placesServiceRef.current.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setDropLatLng({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
        setDestination(description);
        setDropInput(description);
        setDropSuggestions([]);
        setDistance(null);
        setDuration(null);
      }
    });
  };

  const handleSearch = async () => {
    setFareLoading(true);
    setFareError("");
    setFareSearched(true);
    setFareEstimate(null);
    setSelectedService(null);

    try {
      const res = await DispatcherApi.getEstimatedFare({
        s_latitude: pickupLatLng.lat,
        s_longitude: pickupLatLng.lng,
        d_latitude: dropLatLng.lat,
        d_longitude: dropLatLng.lng,
        distance: parseDistanceKm(distance),
        minutes: parseDurationMinutes(duration),
      });
      setFareEstimate(res?.data?.data || res?.data || null);
    } catch (err) {
      setFareError(err?.response?.data?.message || "Erreur lors de la recherche du tarif.");
    } finally {
      setFareLoading(false);
    }
  };

  const handleResetSearch = () => {
    setFareSearched(false);
    setFareEstimate(null);
    setFareError("");
    setSelectedService(null);
    setPaymentMode("");
    setSendError("");
    setSendSuccess(false);
  };

  const handleConfirmRide = async () => {
    if (!selectedService || !paymentMode) return;

    setSendLoading(true);
    setSendError("");
    setSendSuccess(false);

    try {
      await DispatcherApi.sendRideRequest({
        distance:              selectedService?.distance ?? parseDistanceKm(distance),
        minutes:               selectedService?.minutes ?? parseDurationMinutes(duration),
        add_to_fare:           selectedService?.add_to_fare ?? 0,
        estimated_fare:        selectedService?.estimated_fare ?? 0,
        minute_fare:           selectedService?.minute_fare ?? 0,
        distance_fare:         selectedService?.distance_fare ?? 0,
        fare_base:             selectedService?.fare_base ?? 0,
        stripe_transaction_id: "",
        promocode:             "",
        paybywallet:           0,
        userId:                userId || "",
        s_latitude:            pickupLatLng.lat,
        s_longitude:           pickupLatLng.lng,
        s_address:             origin || "",
        d_latitude:            dropLatLng.lat,
        d_longitude:           dropLatLng.lng,
        d_address:             destination || "",
        service_type:          selectedService?.service_id || selectedService?.id || selectedService?._id || "",
        handicap_access:       false,
        travel_pet:            false,
        station_wagon:         false,
        iscorporate:           false,
        payment_mode:          paymentMode,
        pay_id:                "",
        amount_card:           0,
        amount_wallet:         0,
        schedule_date:         "",
        schedule_time:         "",
      });
      setSendSuccess(true);
    } catch (err) {
      setSendError(err?.response?.data?.error || err?.response?.data?.message || "Erreur lors de l'envoi de la demande.");
    } finally {
      setSendLoading(false);
    }
  };

  const toggleDrawer = (drawer, isOpen) => () => {
    if (drawer === "left") setOpen(isOpen);
    if (drawer === "right") setOpenbooking(isOpen);
  };

  const fareList = Array.isArray(fareEstimate)
    ? fareEstimate
    : fareEstimate ? [fareEstimate] : [];

  const searchReady = bothLocationsSet && Boolean(distance) && Boolean(duration) && !fareLoading;

  return (
    <>
      <img
        src="/assets/images/logos/Add-trip.png"
        alt="Open Drawer"
        style={{ width: "100px", height: "80px" }}
        aria-label="open booking drawer"
        onClick={toggleDrawer("right", true)}
        edge="start"
        sx={{ ...(openbooking && { display: "none" }) }}
        className={classes.bookingiconbuttonopen}
      />


      <Drawer className={classes.drawerformbox} variant="persistent" anchor="right" open={openbooking}>
        <Box className={classes.bookingformbox}>

          <div className={classes.booking_header}>
            <Typography variant="h5" component="h2">New Booking</Typography>
          </div>

          <div id="map-temp" style={{ display: "none" }} />

          {/* ── Pickup / Dropoff ── */}
          <div className={classes.autocomplete_control}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, position: "relative", width: "100%" }}>

              <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                <LocationOn sx={{ color: "green", position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                <TextField
                  id="pickuplocation"
                  label="Pickup Location"
                  variant="outlined"
                  size="small"
                  value={pickupInput}
                  onChange={handlePickupChange}
                  className={classes.autocomplete_inputs}
                />
                <Box sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, background: "#fff", boxShadow: 3, maxHeight: 200, overflowY: "auto" }}>
                  {pickupSuggestions.map((s) => (
                    <Box key={s.place_id} sx={{ padding: 1, cursor: "pointer", "&:hover": { backgroundColor: "#eee" } }} onClick={() => handlePickupSelect(s.place_id, s.description)}>
                      {s.description}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ height: 20, width: 2, background: "gray", marginLeft: "18px", opacity: 0.5, borderLeft: "2px dotted gray" }} />

              <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                <LocationOn sx={{ color: "red", position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                <TextField
                  id="drop"
                  label="Drop Location"
                  variant="outlined"
                  size="small"
                  value={dropInput}
                  onChange={handleDropChange}
                  className={classes.autocomplete_inputs}
                />
                <Box sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, background: "#fff", boxShadow: 3, maxHeight: 200, overflowY: "auto" }}>
                  {dropSuggestions.map((s) => (
                    <Box key={s.place_id} sx={{ padding: 1, cursor: "pointer", "&:hover": { backgroundColor: "#eee" } }} onClick={() => handleDropSelect(s.place_id, s.description)}>
                      {s.description}
                    </Box>
                  ))}
                </Box>
              </Box>

            </Box>
          </div>

          {/* ── Distance / Duration ── */}
          <DistanceAndRate
            pickupLatLng={pickupLatLng}
            dropLatLng={dropLatLng}
            origin={origin}
            destination={destination}
            distance={distance}
            duration={duration}
            setMapCenter={setMapCenter}
            setDistance={setDistance}
            setDuration={setDuration}
          />

          {/* ── Section principale : si les deux points sont définis ── */}
          {bothLocationsSet ? (
            <>
              {/* Bouton Rechercher */}
              {!fareSearched && (
                <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={fareLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                    disabled={!searchReady}
                    onClick={handleSearch}
                    sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
                  >
                    {fareLoading ? "Recherche en cours..." : "Rechercher"}
                  </Button>
                  {!distance && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
                      Calcul de la distance en cours…
                    </Typography>
                  )}
                </Box>
              )}

              {/* Résultats */}
              {fareSearched && (
                <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                  {fareLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : fareError ? (
                    <>
                      <Alert severity="error" sx={{ mb: 1 }}>{fareError}</Alert>
                      <Button size="small" onClick={handleResetSearch}>Réessayer</Button>
                    </>
                  ) : fareList.length > 0 ? (
                    <>
                      {/* ── Liste des services cliquables ── */}
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                        Choisir un service
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {fareList.map((item, idx) => {
                          const serviceId = item?.service_id || item?._id || idx;
                          const isSelected = selectedService?.service_id === serviceId
                            || selectedService?._id === serviceId
                            || selectedService === item;
                          return (
                            <Box
                              key={serviceId}
                              onClick={() => setSelectedService(item)}
                              sx={{
                                border: `2px solid ${isSelected ? "#1565c0" : "#e8eaf0"}`,
                                borderRadius: "14px",
                                p: "12px 14px",
                                background: isSelected
                                  ? "linear-gradient(135deg, #e3f0ff 0%, #f0f7ff 100%)"
                                  : "#fff",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: isSelected
                                  ? "0 2px 12px rgba(21,101,192,0.15)"
                                  : "0 1px 4px rgba(0,0,0,0.06)",
                                "&:hover": {
                                  borderColor: "#1565c0",
                                  background: "linear-gradient(135deg, #eef4ff 0%, #f5f9ff 100%)",
                                  boxShadow: "0 2px 12px rgba(21,101,192,0.12)",
                                },
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>

                                {/* Icône véhicule */}
                                <Box sx={{
                                  width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                                  background: isSelected ? "#1565c0" : "#f0f4ff",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  <DirectionsCar sx={{ color: isSelected ? "#fff" : "#1565c0", fontSize: 22 }} />
                                </Box>

                                {/* Infos principales */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#0d1b2a", lineHeight: 1.2 }}>
                                      {item?.service_name || `Service ${idx + 1}`}
                                    </Typography>
                                    {isSelected && <CheckCircle sx={{ color: "#1565c0", fontSize: 18 }} />}
                                  </Box>

                                  {/* Tarif — mis en avant */}
                                  <Typography sx={{ fontWeight: 900, fontSize: 15, color: "#1565c0", mt: 0.3, lineHeight: 1 }}>
                                    {item?.estimated_fare != null
                                      ? `${Number(item.estimated_fare).toLocaleString("fr-FR")} ${item?.currency || CUSTOM_CONSTANTS.BASE_CURRENCY}`
                                      : "--"}
                                  </Typography>

                                  {/* Métadonnées compactes */}
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", mt: 0.6 }}>
                                    {item?.estimated_arrival_time && (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                        <AccessTime sx={{ fontSize: 11, color: "#607d8b" }} />
                                        <Typography variant="caption" sx={{ color: "#607d8b", fontSize: 11 }}>
                                          {item.estimated_arrival_time}
                                        </Typography>
                                      </Box>
                                    )}
                                    {item?.distance != null && (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                        <PinDrop sx={{ fontSize: 11, color: "#607d8b" }} />
                                        <Typography variant="caption" sx={{ color: "#607d8b", fontSize: 11 }}>
                                          {item.distance} {item?.distance_unit || "km"}
                                        </Typography>
                                      </Box>
                                    )}
                                    {item?.minutes != null && (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                        <AccessTime sx={{ fontSize: 11, color: "#607d8b" }} />
                                        <Typography variant="caption" sx={{ color: "#607d8b", fontSize: 11 }}>
                                          {item.minutes} min
                                        </Typography>
                                      </Box>
                                    )}
                                    {item?.seats_available != null && (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                        <EventSeat sx={{ fontSize: 11, color: "#607d8b" }} />
                                        <Typography variant="caption" sx={{ color: "#607d8b", fontSize: 11 }}>
                                          {item.seats_available} place{item.seats_available > 1 ? "s" : ""}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      {/* ── Sélecteur de mode de paiement ── */}
                      {selectedService && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Mode de paiement
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {PAYMENT_MODES.map((mode) => (
                              <Button
                                key={mode}
                                variant={paymentMode === mode ? "contained" : "outlined"}
                                color={paymentMode === mode ? "primary" : "inherit"}
                                onClick={() => setPaymentMode(mode)}
                                sx={{
                                  flex: 1,
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  fontSize: 12,
                                  py: 0.8,
                                }}
                              >
                                {mode}
                              </Button>
                            ))}
                          </Box>

                          {/* ── Bouton Confirmer ── */}
                          {paymentMode && !sendSuccess && (
                            <Box sx={{ mt: 1.5 }}>
                              {sendError && (
                                <Alert severity="error" sx={{ mb: 1 }}>{sendError}</Alert>
                              )}
                              <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                disabled={sendLoading}
                                onClick={handleConfirmRide}
                                sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
                                startIcon={sendLoading ? <CircularProgress size={18} color="inherit" /> : null}
                              >
                                {sendLoading ? "Envoi en cours..." : "Confirmer la réservation"}
                              </Button>
                            </Box>
                          )}

                          {sendSuccess && (
                            <Alert severity="success" sx={{ mt: 1.5 }} icon={<CheckCircle />}>
                              Demande envoyée avec succès !
                            </Alert>
                          )}
                        </Box>
                      )}

                      <Button size="small" variant="text" onClick={handleResetSearch} sx={{ mt: 1.5, display: "block" }}>
                        ← Modifier la recherche
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert severity="info">Aucun tarif disponible pour ce trajet.</Alert>
                      <Button size="small" onClick={handleResetSearch} sx={{ mt: 1 }}>Modifier la recherche</Button>
                    </>
                  )}
                </Box>
              )}
            </>
          ) : null}

        </Box>
      </Drawer>
    </>
  );
};

export default BookingForm;
